/**
 * PATH: packages/bills/src/components/BillDataPanelHeader.tsx
 * FILE_ID: BILLS-PKG-CMP-HEADER-011
 * PURPOSE: Pinned header for the bill data panel — a modern summary card (vendor
 *          monogram, identity, status pill, headline total) plus the action bar
 *          (Save / Approve / Reject / Undo / Redo / Re-extract / Not-a-bill /
 *          Push to Xero).
 *
 * WHY: The header stays visible while the form body scrolls, so the bookkeeper
 *      never has to scroll to reach Approve/Reject/Push. The disabled state of
 *      each action encodes the bill's status machine. All state + handlers live
 *      in BillDataPanel; this component is a pure props-in / markup-out view.
 *
 * FLOW: bill + status flags → summary card + action bar → callbacks up
 */

import { Check, Redo2, RotateCcw, Save, Undo2, UploadCloud, X } from 'lucide-react';
import { Badge, Button } from '@quotech/ui';
import type { BillDetail } from '../types';
import { formatMoney } from './format-currency';
import { formatShortDate } from './format-datetime';
import { ReceivedMetaRow, StatusBadge } from './BillPanelChrome';
import { ReclassifyMenu } from './ReclassifyMenu';

interface BillDataPanelHeaderProps {
  bill: BillDetail;
  status: BillDetail['status'];
  isDirty: boolean;
  busy: boolean;
  canUndo: boolean;
  canRedo: boolean;
  canApprove: boolean;
  canReject: boolean;
  canPush: boolean;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onApprove: () => Promise<void> | void;
  onReject: () => Promise<void> | void;
  onPushToXero: () => Promise<void> | void;
  onReExtract?: () => Promise<void>;
  onReclassify?: (category: string) => Promise<void>;
}

// Shared sizing for the action-bar buttons — compact h-8, rounded-md, with a
// small icon gap. Composed over the @quotech/ui Button so light + dark tokens
// keep working; tailwind-merge dedupes the size overrides.
const ACTION_BTN = 'h-8 gap-1.5 rounded-md px-2.5 text-xs';
const QUIET_BTN = ACTION_BTN + ' text-muted-foreground hover:text-foreground';

/** Two-letter monogram from the vendor name ("H&G Concrete Supplies" → "HG"). */
function vendorInitials(name?: string | null): string {
  const words = (name ?? '')
    .replace(/[^\p{L}\p{N} ]+/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '—';
  const first = words[0]?.[0] ?? '';
  const second = words.length > 1 ? (words[1]?.[0] ?? '') : (words[0]?.[1] ?? '');
  return (first + second).toUpperCase() || '—';
}

export function BillDataPanelHeader({
  bill,
  status,
  isDirty,
  busy,
  canUndo,
  canRedo,
  canApprove,
  canReject,
  canPush,
  onSave,
  onUndo,
  onRedo,
  onApprove,
  onReject,
  onPushToXero,
  onReExtract,
  onReclassify,
}: BillDataPanelHeaderProps) {
  const total = formatMoney(bill.total, bill.currency);
  const due = formatShortDate(bill.due_date);
  const invoicePart = bill.invoice_number
    ? `Invoice ${bill.invoice_number}`
    : 'No invoice number';
  const subline = [invoicePart, due ? `Due ${due}` : null].filter(Boolean).join('  ·  ');
  const hasMeta = Boolean(bill.email_received_at || bill.xero_invoice_number || bill.xero_push_error);

  return (
    /*
      Pinned header — summary card + action bar at the top of the rail so the
      bookkeeper never has to scroll to Approve/Reject/Push. The form body
      below scrolls independently. This is a normal flex header above the
      scroll body (NOT position: sticky — the aside isn't the scroll ancestor).
    */
    <header className="shrink-0 space-y-2.5 border-b border-border bg-muted/30 px-3 pt-3 pb-2.5">
      {/* Summary card — monogram · identity + status · headline total. */}
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card p-2.5 shadow-sm">
        <span
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-semibold tracking-tight text-primary"
        >
          {vendorInitials(bill.vendor_name)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="min-w-0 truncate text-sm font-semibold tracking-tight">
              {bill.vendor_name || 'Untitled vendor'}
            </h2>
            <span className="shrink-0">
              <StatusBadge status={status} />
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{subline}</p>
        </div>
        {total && (
          <div className="shrink-0 pl-2 text-right">
            <p className="text-base font-semibold tabular-nums tracking-tight">{total}</p>
          </div>
        )}
      </div>

      {hasMeta && (
        <div className="space-y-1.5 px-0.5">
          <ReceivedMetaRow bill={bill} />
          {bill.xero_invoice_number && (
            <Badge tone="success" className="max-w-full rounded-full">
              <span className="truncate">Pushed to Xero · #{bill.xero_invoice_number}</span>
            </Badge>
          )}
          {bill.xero_push_error && (
            <p className="break-words text-xs text-destructive">
              Xero error: {bill.xero_push_error}
            </p>
          )}
        </div>
      )}

      {/* Action bar — TWO deliberate rows, not one accidental flex-wrap. The
          rail is narrow, so a single row always wrapped at an arbitrary point
          and the ml-auto cluster jumped to the right edge leaving a ragged
          hole. Row 1: the three primary decisions stretched edge-to-edge as an
          equal-width group. Row 2: draft history (undo/redo) + the quiet
          secondary actions, also stretched so both rows share clean edges.
          Disabled state still encodes the bill's status machine
          (extracted/reviewed → approve; reviewed-only → push; never-pushed →
          reject). */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            className={ACTION_BTN + ' flex-1'}
            disabled={!isDirty || busy}
            onClick={onSave}
            title={isDirty ? 'Save changes' : 'No changes to save'}
          >
            <Save className="h-3.5 w-3.5" aria-hidden />
            Save
          </Button>
          {/* Approve = positive terminal action (soft emerald); Reject =
              destructive (soft red outline). Tasteful tints, not saturated
              blocks — the filled primary Save is the one bold anchor. */}
          <Button
            variant="outline"
            size="sm"
            className={
              ACTION_BTN +
              ' flex-1 border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/60'
            }
            disabled={!canApprove || busy}
            onClick={onApprove}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
            Approve
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={
              ACTION_BTN +
              ' flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/40'
            }
            disabled={!canReject || busy}
            onClick={onReject}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Reject
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {/* Undo / redo the edit draft — the discoverable "option" for the
              operator who couldn't ⌘Z a wrong PDF pick back out of a field. */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 shrink-0 rounded-md px-0 text-muted-foreground hover:text-foreground"
            disabled={!canUndo || busy}
            onClick={onUndo}
            title="Undo last change (⌘Z)"
            aria-label="Undo last change"
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 shrink-0 rounded-md px-0 text-muted-foreground hover:text-foreground"
            disabled={!canRedo || busy}
            onClick={onRedo}
            title="Redo (⌘⇧Z)"
            aria-label="Redo change"
          >
            <Redo2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
          {onReExtract && status !== 'pushed' && (
            <Button
              variant="outline"
              size="sm"
              className={QUIET_BTN + ' flex-1'}
              disabled={busy}
              onClick={onReExtract}
              title={
                bill.status === 'failed'
                  ? 'Retry AI extraction on the source email'
                  : 'Re-run AI extraction (overwrites current fields)'
              }
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Re-extract
            </Button>
          )}
          {/*
            Reclassify dropdown — "this isn't a bill, it's our outgoing invoice /
            a remittance / spam". Hidden once the bill is already pushed to Xero
            (reclassifying then would mislead: the AP record exists in Xero and
            the bill row stays put even if the email category changes; see
            BillListView._cleanup_orphaned_pending which deliberately doesn't
            sweep reviewed/pushed rows).
          */}
          {onReclassify && status !== 'pushed' && (
            <ReclassifyMenu
              key={`${bill.id}:${bill.extracted_at ?? ''}`}
              busy={busy}
              onReclassify={onReclassify}
              className="flex-1"
            />
          )}
          <Button
            variant="outline"
            size="sm"
            className={QUIET_BTN + ' flex-1'}
            disabled={!canPush || busy}
            onClick={onPushToXero}
            title={
              bill.xero_invoice_id
                ? 'Already pushed'
                : status !== 'reviewed'
                  ? 'Approve first'
                  : 'Push to Xero'
            }
          >
            <UploadCloud className="h-3.5 w-3.5" aria-hidden />
            Push to Xero
          </Button>
        </div>
      </div>
    </header>
  );
}
